// =============================================================================
// DesignerSettingsPage — Per-store designer checkout settings
// =============================================================================
// Lets a merchant configure the storefront designer's order-confirmation
// modal for each connected store: the "Print Anyway" acknowledgement message,
// an optional terms checkbox, and up to two custom questions per input type
// (checkbox / select / text). Answers to questions marked "add to order" are
// appended to the Shopify line item properties at add-to-cart time.
//
// Settings are STORE-LEVEL (`Connection.settings.designerCheckout`) — the
// page starts with a store picker. The embedded Shopify app edits the same
// data from its own Settings → Designer tab.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Select,
  Spinner,
  useConfirm,
} from '@tkpod/ui';
import {
  DESIGNER_CHECKOUT_LIMITS,
  DESIGNER_CHECKOUT_MAX_PER_TYPE,
  hasAnyPermission,
  isValidLineItemLabel,
  type DesignerCheckoutInput,
  type DesignerCheckoutQuestionType,
  type DesignerCheckoutStored,
  type ResolvedDesignerCheckout,
} from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { useConnections } from '../hooks/use-connections';

/** API shape returned by GET .../connections/:id/designer-checkout. */
interface EditableCheckout {
  stored: DesignerCheckoutStored;
  resolved: ResolvedDesignerCheckout;
}

/** Local editing state for one question row. */
interface QuestionDraft {
  /** Server id when the question already exists; undefined for new rows. */
  id?: string;
  /** Stable local key for React lists (new rows have no server id yet). */
  key: string;
  type: DesignerCheckoutQuestionType;
  label: string;
  /** SELECT only: one option per line. */
  optionsText: string;
  required: boolean;
  active: boolean;
  addToOrder: boolean;
  /** Short Shopify line item property key (required when addToOrder). */
  lineItemLabel: string;
}

const QUESTION_TYPE_LABELS: Record<DesignerCheckoutQuestionType, string> = {
  CHECKBOX: 'Checkbox',
  SELECT: 'Select box',
  TEXT: 'Text input',
};

const DEFAULT_PRINT_ANYWAY_PLACEHOLDER =
  'I have reviewed the quality warnings and want to print my design as is.';

let draftKeyCounter = 0;
const nextDraftKey = () => `draft-${++draftKeyCounter}`;

function endpointFor(connectionId: string): string {
  return `/api/v1/organizations/current/connections/${connectionId}/designer-checkout`;
}

function toDrafts(stored: DesignerCheckoutStored): QuestionDraft[] {
  return (stored.questions ?? []).map((q) => ({
    id: q.id,
    key: q.id,
    type: q.type,
    label: q.label,
    optionsText: (q.options ?? []).join('\n'),
    required: q.required,
    active: q.active,
    addToOrder: q.addToOrder,
    lineItemLabel: q.lineItemLabel ?? '',
  }));
}

export function DesignerSettingsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const permissions = user?.currentOrganization?.permissions ?? [];
  const canEdit = hasAnyPermission(permissions, ['settings:update', 'settings:manage']);

  const connectionsQuery = useConnections();
  const shopifyConnections = useMemo(
    () => (connectionsQuery.data ?? []).filter((c) => c.type === 'SHOPIFY'),
    [connectionsQuery.data],
  );

  const [connectionId, setConnectionId] = useState<string>('');
  useEffect(() => {
    if (!connectionId && shopifyConnections.length > 0) {
      setConnectionId(shopifyConnections[0].id);
    }
  }, [connectionId, shopifyConnections]);

  const checkoutQuery = useQuery<EditableCheckout>({
    queryKey: ['organization', 'designer-checkout', connectionId],
    queryFn: () => api.get<EditableCheckout>(endpointFor(connectionId)),
    enabled: !!connectionId,
  });

  // --- Local form state (re-initialized whenever the store or data changes) --
  const [printAnywayMessage, setPrintAnywayMessage] = useState('');
  const [termsMessage, setTermsMessage] = useState('');
  const [questions, setQuestions] = useState<QuestionDraft[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const initializedForRef = useRef<string | null>(null);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!checkoutQuery.data || initializedForRef.current === connectionId) return;
    initializedForRef.current = connectionId;
    setPrintAnywayMessage(checkoutQuery.data.stored.printAnywayMessage ?? '');
    setTermsMessage(checkoutQuery.data.stored.termsMessage ?? '');
    setQuestions(toDrafts(checkoutQuery.data.stored));
    setFormError(null);
  }, [checkoutQuery.data, connectionId]);

  const handleStoreChange = (nextId: string) => {
    initializedForRef.current = null;
    setConnectionId(nextId);
    setFormError(null);
    setSuccessMessage(null);
  };

  const flashSuccess = (message: string) => {
    setSuccessMessage(message);
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000);
  };

  const updateMutation = useMutation<EditableCheckout, Error, DesignerCheckoutInput>({
    mutationFn: (input) => api.put<EditableCheckout>(endpointFor(connectionId), input),
    onSuccess: (data) => {
      queryClient.setQueryData(['organization', 'designer-checkout', connectionId], data);
      setPrintAnywayMessage(data.stored.printAnywayMessage ?? '');
      setTermsMessage(data.stored.termsMessage ?? '');
      setQuestions(toDrafts(data.stored));
      flashSuccess('Designer settings saved.');
    },
  });

  const countByType = (type: DesignerCheckoutQuestionType) =>
    questions.filter((q) => q.type === type).length;

  /** True when another question of this type already adds to the order line. */
  const typeHasOtherAddToOrder = (type: DesignerCheckoutQuestionType, key?: string) =>
    questions.some((q) => q.type === type && q.addToOrder && q.key !== key);

  const addQuestion = (type: DesignerCheckoutQuestionType) => {
    if (countByType(type) >= DESIGNER_CHECKOUT_MAX_PER_TYPE) return;
    setQuestions((prev) => [
      ...prev,
      {
        key: nextDraftKey(),
        type,
        label: '',
        optionsText: '',
        required: false,
        active: true,
        // Only one question per type may append its answer to the order
        // line item — default on only when the slot is free.
        addToOrder: !typeHasOtherAddToOrder(type),
        lineItemLabel: '',
      },
    ]);
  };

  const updateQuestion = (key: string, patch: Partial<QuestionDraft>) => {
    setQuestions((prev) => prev.map((q) => (q.key === key ? { ...q, ...patch } : q)));
  };

  const removeQuestion = async (key: string) => {
    const target = questions.find((q) => q.key === key);
    if (!target) return;
    if (target.id) {
      const ok = await confirm({
        title: 'Delete question',
        description:
          'The question will be removed from the order confirmation for new orders. This takes effect when you save.',
        confirmLabel: 'Delete',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setQuestions((prev) => prev.filter((q) => q.key !== key));
  };

  const handleSave = () => {
    setFormError(null);
    setSuccessMessage(null);

    const trimmedPrintAnyway = printAnywayMessage.trim();
    const trimmedTerms = termsMessage.trim();
    if (trimmedPrintAnyway.length > DESIGNER_CHECKOUT_LIMITS.messageMaxLength) {
      setFormError(
        `The Print Anyway message must be at most ${DESIGNER_CHECKOUT_LIMITS.messageMaxLength} characters.`,
      );
      return;
    }
    if (trimmedTerms.length > DESIGNER_CHECKOUT_LIMITS.messageMaxLength) {
      setFormError(
        `The terms message must be at most ${DESIGNER_CHECKOUT_LIMITS.messageMaxLength} characters.`,
      );
      return;
    }

    const payloadQuestions: NonNullable<DesignerCheckoutInput['questions']> = [];
    for (const q of questions) {
      const label = q.label.trim();
      if (!label) {
        setFormError('Every question needs a label.');
        return;
      }
      const options = q.optionsText
        .split('\n')
        .map((o) => o.trim())
        .filter(Boolean);
      if (q.type === 'SELECT' && options.length < 2) {
        setFormError(`"${label}" needs at least 2 select options (one per line).`);
        return;
      }
      if (options.length > DESIGNER_CHECKOUT_LIMITS.selectOptionsMax) {
        setFormError(
          `"${label}" can have at most ${DESIGNER_CHECKOUT_LIMITS.selectOptionsMax} options.`,
        );
        return;
      }
      const lineItemLabel = q.lineItemLabel.trim();
      if (q.addToOrder && !isValidLineItemLabel(lineItemLabel)) {
        setFormError(
          `"${label}" needs a short line item label (max ` +
            `${DESIGNER_CHECKOUT_LIMITS.lineItemLabelMaxLength} characters — about ` +
            `three words, no leading underscore, no brackets) to appear on the ` +
            `Shopify order.`,
        );
        return;
      }
      payloadQuestions.push({
        ...(q.id ? { id: q.id } : {}),
        type: q.type,
        label,
        ...(q.type === 'SELECT' ? { options } : {}),
        required: q.required,
        active: q.active,
        addToOrder: q.addToOrder,
        ...(lineItemLabel ? { lineItemLabel } : {}),
      });
    }

    // One "add to order" question per type — mirrors the server-side rule.
    for (const type of ['CHECKBOX', 'SELECT', 'TEXT'] as const) {
      const count = payloadQuestions.filter((q) => q.type === type && q.addToOrder).length;
      if (count > 1) {
        setFormError(
          `Only one ${QUESTION_TYPE_LABELS[type].toLowerCase()} question can add its answer to the order line item.`,
        );
        return;
      }
    }

    updateMutation.mutate({
      printAnywayMessage: trimmedPrintAnyway ? trimmedPrintAnyway : null,
      termsMessage: trimmedTerms ? trimmedTerms : null,
      questions: payloadQuestions,
    });
  };

  if (!user?.currentOrganization) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
        No organization found. Please contact support.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {dialog}

      <Card>
        <CardHeader
          title="Designer Settings"
          description="Configure the order confirmation shown in the storefront designer: the Print Anyway message, an optional terms checkbox, and custom questions whose answers can be attached to Shopify order line items."
        />

        <div className="space-y-6 p-6 pt-2">
          {successMessage && (
            <div className="rounded-md bg-success/10 px-4 py-3 text-sm text-success">
              {successMessage}
            </div>
          )}
          {(formError || updateMutation.error) && (
            <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
              {formError ?? updateMutation.error?.message ?? 'Something went wrong.'}
            </div>
          )}
          {!canEdit && (
            <div className="rounded-md bg-warning/10 px-4 py-3 text-sm text-warning">
              You can view these settings but need the “settings:update” permission to make
              changes.
            </div>
          )}

          {/* Store picker --------------------------------------------------- */}
          {connectionsQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : shopifyConnections.length === 0 ? (
            <div className="rounded-md bg-surface-secondary px-4 py-3 text-sm text-text-secondary">
              No connected Shopify stores yet. Connect a store first — designer checkout
              settings are configured per store.
            </div>
          ) : (
            <div className="max-w-sm">
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Store
              </label>
              <Select
                value={connectionId}
                onChange={(e) => handleStoreChange(e.target.value)}
                options={shopifyConnections.map((c) => ({
                  value: c.id,
                  label: c.shopDomain ? `${c.name} (${c.shopDomain})` : c.name,
                }))}
              />
            </div>
          )}

          {connectionId && checkoutQuery.isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          )}
          {connectionId && checkoutQuery.isError && (
            <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
              {checkoutQuery.error instanceof Error
                ? checkoutQuery.error.message
                : 'Failed to load designer settings. Please try again later.'}
            </div>
          )}

          {connectionId && checkoutQuery.data && (
            <>
              {/* Messages --------------------------------------------------- */}
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-primary">
                    “Print Anyway” message
                  </label>
                  <textarea
                    value={printAnywayMessage}
                    onChange={(e) => setPrintAnywayMessage(e.target.value)}
                    disabled={!canEdit}
                    rows={2}
                    maxLength={DESIGNER_CHECKOUT_LIMITS.messageMaxLength}
                    placeholder={DEFAULT_PRINT_ANYWAY_PLACEHOLDER}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-primary disabled:opacity-60"
                  />
                  <p className="mt-1 text-xs text-text-tertiary">
                    Shown next to the acknowledgement checkbox when a design has quality
                    warnings (overlap or low resolution). Leave empty to use the default
                    text.
                  </p>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text-primary">
                    Terms message (optional)
                  </label>
                  <textarea
                    value={termsMessage}
                    onChange={(e) => setTermsMessage(e.target.value)}
                    disabled={!canEdit}
                    rows={2}
                    maxLength={DESIGNER_CHECKOUT_LIMITS.messageMaxLength}
                    placeholder="e.g. I confirm that I own the rights to the uploaded artwork."
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-primary disabled:opacity-60"
                  />
                  <p className="mt-1 text-xs text-text-tertiary">
                    When set, customers must tick this checkbox before adding to cart.
                    Leave empty to hide the terms row.
                  </p>
                </div>
              </div>

              {/* Custom questions -------------------------------------------- */}
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-text-primary">Custom questions</p>
                  <div className="flex gap-2">
                    {(['CHECKBOX', 'SELECT', 'TEXT'] as const).map((type) => (
                      <Button
                        key={type}
                        variant="outline"
                        size="sm"
                        disabled={
                          !canEdit || countByType(type) >= DESIGNER_CHECKOUT_MAX_PER_TYPE
                        }
                        onClick={() => addQuestion(type)}
                      >
                        + {QUESTION_TYPE_LABELS[type]}
                      </Button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-text-tertiary">
                  Up to {DESIGNER_CHECKOUT_MAX_PER_TYPE} questions per type. Inactive
                  questions are kept here but never shown to customers.
                </p>

                {questions.length === 0 ? (
                  <div className="rounded-md bg-surface-secondary px-4 py-3 text-sm text-text-secondary">
                    No custom questions yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions.map((q) => (
                      <div
                        key={q.key}
                        className="space-y-3 rounded-lg border border-border p-4"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="default">{QUESTION_TYPE_LABELS[q.type]}</Badge>
                            {!q.active && <Badge variant="warning">Inactive</Badge>}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!canEdit}
                            onClick={() => void removeQuestion(q.key)}
                          >
                            Delete
                          </Button>
                        </div>

                        <Input
                          value={q.label}
                          onChange={(e) => updateQuestion(q.key, { label: e.target.value })}
                          disabled={!canEdit}
                          maxLength={DESIGNER_CHECKOUT_LIMITS.questionLabelMaxLength}
                          placeholder={
                            q.type === 'CHECKBOX'
                              ? 'e.g. Remove background from my image'
                              : q.type === 'SELECT'
                                ? 'e.g. Garment color'
                                : 'e.g. Any notes for our print team?'
                          }
                        />

                        {q.type === 'SELECT' && (
                          <div>
                            <label className="mb-1 block text-xs font-medium text-text-secondary">
                              Options (one per line)
                            </label>
                            <textarea
                              value={q.optionsText}
                              onChange={(e) =>
                                updateQuestion(q.key, { optionsText: e.target.value })
                              }
                              disabled={!canEdit}
                              rows={3}
                              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-primary disabled:opacity-60"
                            />
                          </div>
                        )}

                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-text-primary">
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={q.required}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateQuestion(q.key, { required: e.target.checked })
                              }
                            />
                            Required
                          </label>
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              checked={q.active}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateQuestion(q.key, { active: e.target.checked })
                              }
                            />
                            Active
                          </label>
                          <label
                            className={`flex items-center gap-2 ${
                              !q.addToOrder && typeHasOtherAddToOrder(q.type, q.key)
                                ? 'cursor-not-allowed opacity-60'
                                : 'cursor-pointer'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={q.addToOrder}
                              disabled={
                                !canEdit ||
                                (!q.addToOrder && typeHasOtherAddToOrder(q.type, q.key))
                              }
                              onChange={(e) =>
                                updateQuestion(q.key, { addToOrder: e.target.checked })
                              }
                            />
                            Add answer to Shopify order line item
                          </label>
                        </div>

                        {q.addToOrder && (
                          <div>
                            <label className="mb-1 block text-xs font-medium text-text-secondary">
                              Line item label
                            </label>
                            <Input
                              value={q.lineItemLabel}
                              onChange={(e) =>
                                updateQuestion(q.key, { lineItemLabel: e.target.value })
                              }
                              disabled={!canEdit}
                              maxLength={DESIGNER_CHECKOUT_LIMITS.lineItemLabelMaxLength}
                              placeholder="e.g. Background removal"
                            />
                            <p className="mt-1 text-xs text-text-tertiary">
                              Shown on the Shopify order as the property name — keep it
                              short (about three words, max{' '}
                              {DESIGNER_CHECKOUT_LIMITS.lineItemLabelMaxLength} characters).
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={!canEdit || updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save settings'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
